import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Mic, Plus, CheckCircle } from 'lucide-react';
import { useStoryStore } from '../../store/storyStore';
import { useSignedUrl } from '../../hooks/useSignedUrl';

interface StoryPickerSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateStory: () => void;
}

const StoryItemWithPhoto: React.FC<{
  story: any;
  onClick: () => void;
}> = ({ story, onClick }) => {
  const { url: photoUrl } = useSignedUrl(story.photo_url, 'photos');

  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 p-4 bg-white rounded-xl border border-stone-200 hover:border-rose-300 hover:shadow-md transition-all active:scale-[0.98]"
    >
      <div className="flex-shrink-0">
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={story.title}
            className="w-14 h-14 rounded-full object-cover border-2 border-stone-100"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center border-2 border-stone-100">
            <span className="text-lg font-bold text-stone-500">
              {story.title.charAt(0)}
            </span>
          </div>
        )}
      </div>

      <div className="flex-1 text-left min-w-0">
        <h3 className="font-semibold text-stone-900 truncate">
          {story.title}
        </h3>
        <p className="text-sm text-stone-500 truncate">
          {story.relationship}
        </p>
        <div className="flex items-center gap-2 mt-1">
          <div className="flex-1 h-1.5 bg-stone-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                story.progress === 100 ? 'bg-teal-500' : 'bg-rose-500'
              }`}
              style={{ width: `${story.progress}%` }}
            />
          </div>
          <span className="text-xs font-medium text-stone-600">
            {story.progress}%
          </span>
        </div>
      </div>

      <div className="flex-shrink-0">
        {story.progress === 100 ? (
          <CheckCircle className="h-6 w-6 text-teal-500" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
            <Mic className="h-5 w-5 text-rose-600" />
          </div>
        )}
      </div>
    </button>
  );
};

const StoryPickerSheet: React.FC<StoryPickerSheetProps> = ({
  isOpen,
  onClose,
  onCreateStory
}) => {
  const navigate = useNavigate();
  const { stories } = useStoryStore();
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleSelectStory = (storyId: string) => {
    onClose();
    navigate(`/story-recorder/${storyId}?openOverlay=true`);
  };

  const handleCreateStory = () => {
    onClose();
    onCreateStory();
  };

  const sortedStories = [...stories].sort((a, b) => {
    if (a.progress === 100 && b.progress !== 100) return 1;
    if (a.progress !== 100 && b.progress === 100) return -1;
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  const incompleteCount = stories.filter(s => s.progress < 100).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl animate-slide-up"
        style={{
          maxHeight: '85dvh',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}
      >
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-12 h-1.5 bg-stone-300 rounded-full" />
        </div>

        <div className="px-6 pb-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-stone-900">
              Selecciona una historia
            </h2>
            <p className="text-sm text-stone-500 mt-0.5">
              {incompleteCount > 0
                ? `${incompleteCount} historia${incompleteCount > 1 ? 's' : ''} pendiente${incompleteCount > 1 ? 's' : ''}`
                : 'Todas las historias completadas'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-full transition-colors flex items-center justify-center"
          >
            <X className="h-5 w-5 text-stone-600" />
          </button>
        </div>

        <div className="px-6 pb-6 overflow-y-auto" style={{ maxHeight: 'calc(85dvh - 120px)' }}>
          <div className="space-y-3">
            {sortedStories.map((story) => (
              <StoryItemWithPhoto
                key={story.id}
                story={story}
                onClick={() => handleSelectStory(story.id)}
              />
            ))}

            <button
              onClick={handleCreateStory}
              className="w-full flex items-center gap-4 p-4 bg-stone-50 rounded-xl border-2 border-dashed border-stone-300 hover:border-rose-400 hover:bg-rose-50/50 transition-all active:scale-[0.98]"
            >
              <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center">
                <Plus className="h-6 w-6 text-rose-600" />
              </div>
              <div className="text-left">
                <h3 className="font-semibold text-stone-900">
                  Crear nueva historia
                </h3>
                <p className="text-sm text-stone-500">
                  Agrega a un nuevo familiar
                </p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StoryPickerSheet;
